import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { usePrimeStream, type PrimeStreamFilters } from "../api/primeStream";
import { FilterBar } from "../components/FilterBar";
import { MessageBlock } from "../components/MessageBlock";

function filtersFromParams(params: URLSearchParams): PrimeStreamFilters {
  return {
    sessionId: params.get("session_id") ?? undefined,
    since: params.get("since") ?? undefined,
    until: params.get("until") ?? undefined,
  };
}

function paramsFromFilters(filters: PrimeStreamFilters): URLSearchParams {
  const p = new URLSearchParams();
  if (filters.sessionId) p.set("session_id", filters.sessionId);
  if (filters.since) p.set("since", filters.since);
  if (filters.until) p.set("until", filters.until);
  return p;
}

export function PrimeThreadReader() {
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = useMemo(() => filtersFromParams(searchParams), [searchParams]);

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading, error } =
    usePrimeStream(filters);

  function handleApply(next: PrimeStreamFilters) {
    setSearchParams(paramsFromFilters(next));
  }

  const allItems = data?.pages.flatMap((p) => p.items) ?? [];

  return (
    <div>
      <FilterBar initial={filters} onApply={handleApply} />

      {isLoading && <div className="status-line">Loading…</div>}
      {error && (
        <div className="status-line error">
          Error: {error instanceof Error ? error.message : String(error)}
        </div>
      )}
      {!isLoading && !error && allItems.length === 0 && (
        <div className="status-line">No messages match those filters.</div>
      )}

      <div className="message-list">
        {allItems.map((item) => (
          <MessageBlock key={item.id} item={item} />
        ))}
      </div>

      {hasNextPage && (
        <div className="load-more">
          <button
            type="button"
            className="btn"
            onClick={() => fetchNextPage()}
            disabled={isFetchingNextPage}
          >
            {isFetchingNextPage ? "Loading…" : "Load older"}
          </button>
        </div>
      )}
    </div>
  );
}
